import { useMemo, type ReactNode } from "react";
import { useAlertDialog } from "context/AlertDialogContext";
import { handleToast } from "utils/toasts";
import { TOAST_MESSAGES } from "features/script-editor/constants";
import { commentErrorMessage } from "features/script-editor/lib/comment-error-message";
import { ScriptEditorCommentsDialog } from "features/script-editor/components/script-editor-comments-dialog";
import { ScriptCommentsPanel } from "features/script-editor/components/script-comments-panel";
import { parseDocumentCommentsListResponse } from "features/script-editor/parseDocumentCommentsListResponse";
import { computeCommentHeaderBadge } from "features/script-editor/script-comment-thread-reads";
import {
  useCommentThreadReads,
  useCreateComment,
  useDeleteComment,
  useReopenComment,
  useResolveComment,
  useUpdateComment,
} from "features/script-editor/hooks/comments";
import {
  type ProjectCommentsBadge,
  useRegisterProjectCommentsChrome,
} from "features/project-comments/project-comments-chrome";
import { useAllDocumentComments } from "hooks/project/useAllDocumentComments";
import {
  useProjectCommentsShellActions,
  useProjectCommentsShellState,
  useResetProjectCommentsShellOnScopeChange,
} from "hooks/project/useProjectCommentsShell";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { Permissions } from "utils/permissions/permissionConstants";
import { useSelector } from "react-redux";
import type { AppState } from "store/reducers/Reducer";

export function ProjectCommentsShell({ children }: { children: ReactNode }) {
  const { projectId, documentId, hasValidIds } = useProjectIdentifiers();
  const pid = projectId != null ? String(projectId) : "";
  const did = documentId != null ? String(documentId) : "";
  const enabled = hasValidIds;

  const { canProject } = useProjectAuth(pid || undefined);
  const canComment = canProject(Permissions.COMMENT_CREATE);
  const canResolve = canProject(Permissions.COMMENT_RESOLVE);
  const canModerate = canProject(Permissions.COMMENT_MODERATE);
  const currentUserId = useSelector((s: AppState) => s.auth.user_id);

  const shellState = useProjectCommentsShellState(pid, did, enabled);
  const {
    openComments,
    setCommentsOpen,
    clearScrollToThreadRequest,
    clearPendingScheduleRootAnchor,
    goToScreenplayAnchor,
    goToScheduleAnchor,
  } = useProjectCommentsShellActions();

  useResetProjectCommentsShellOnScopeChange();

  const commentsQuery = useAllDocumentComments(pid, did, enabled, {}, shellState.isOpen);
  const threadReadsQuery = useCommentThreadReads(pid, did, enabled);
  const createComment = useCreateComment();
  const resolveComment = useResolveComment();
  const reopenComment = useReopenComment();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();
  const { showAlertDialog } = useAlertDialog();

  const allComments = useMemo(
    () => parseDocumentCommentsListResponse(commentsQuery.data),
    [commentsQuery.data]
  );

  // Without read times every thread would count as unread, so the unread count stays unknown
  // until the first load succeeds. The open-thread count needs no read times. The comments
  // panel shows a failure with Try again. A failed refetch keeps the read times that loaded.
  const readTimes = threadReadsQuery.data;
  const readStateLoadFailed = threadReadsQuery.isError && readTimes === undefined;
  const commentsBadge = useMemo<ProjectCommentsBadge>(() => {
    const badge = computeCommentHeaderBadge(allComments, readTimes ?? {}, currentUserId);
    if (readTimes !== undefined) return badge;
    return { ...badge, unreadThreadCount: readStateLoadFailed ? "unavailable" : "loading" };
  }, [allComments, readTimes, currentUserId, readStateLoadFailed]);

  useRegisterProjectCommentsChrome((tab) => {
    if (tab === "comments") {
      openComments();
    }
  }, commentsBadge);

  const handleCreateRootComment = async (body: string) => {
    const anchor = shellState.pendingScheduleRootAnchor;
    if (!anchor) return;

    try {
      await createComment.mutateAsync({
        projectId: pid,
        documentId: did,
        payload: {
          body,
          feature: "schedule",
          target_type: "stripboard_item",
          target_id: anchor.target_id,
          scene_id: anchor.scene_id,
          anchor_text: anchor.anchor_text,
        },
      });
      clearPendingScheduleRootAnchor();
    } catch (error) {
      handleToast(commentErrorMessage(error, "post"), "error");
      throw error;
    }
  };

  const handleCreateReply = async (commentId: string, body: string) => {
    const parentComment = allComments.find((comment) => comment.comment_id === commentId);
    const anchor = parentComment?.anchors?.[0];
    if (!anchor) return;

    try {
      await createComment.mutateAsync({
        projectId: pid,
        documentId: did,
        payload: {
          parent_comment_id: commentId,
          body,
          feature: anchor.feature,
          target_type: anchor.target_type,
          target_id: anchor.target_id ?? undefined,
          scene_id: anchor.scene_id ?? undefined,
          anchor_text: anchor.anchor_text ?? undefined,
          paragraph_id: anchor.paragraph_id ?? undefined,
        },
      });
    } catch (error) {
      handleToast(commentErrorMessage(error, "reply"), "error");
      throw error;
    }
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <ScriptEditorCommentsDialog
        open={shellState.isOpen}
        onOpenChange={setCommentsOpen}
        commentsPanel={
          <ScriptCommentsPanel
            key="project-comments"
            projectId={pid}
            documentId={did}
            comments={allComments}
            commentsScope="project"
            isCreatingComment={createComment.isPending}
            isResolvingComment={resolveComment.isPending}
            isReopeningComment={reopenComment.isPending}
            canComment={canComment}
            canResolve={canResolve}
            canModerate={canModerate}
            listLoadFailed={commentsQuery.isError}
            onRetryListLoad={() => void commentsQuery.refetch()}
            onCreateReply={handleCreateReply}
            onResolveComment={async (commentId) => {
              try {
                await resolveComment.mutateAsync({ projectId: pid, documentId: did, commentId });
              } catch (error) {
                handleToast(commentErrorMessage(error, "resolve"), "error");
              }
            }}
            onReopenComment={async (commentId) => {
              try {
                await reopenComment.mutateAsync({ projectId: pid, documentId: did, commentId });
              } catch (error) {
                handleToast(commentErrorMessage(error, "reopen"), "error");
              }
            }}
            scrollToThreadRequest={shellState.scrollToThreadRequest}
            onScrollToThreadHandled={clearScrollToThreadRequest}
            onGoToAnchor={goToScreenplayAnchor}
            onGoToScheduleAnchor={goToScheduleAnchor}
            pendingRootAnchor={
              shellState.pendingScheduleRootAnchor
                ? { anchorText: shellState.pendingScheduleRootAnchor.anchor_text }
                : null
            }
            onCreateRootComment={handleCreateRootComment}
            onClearPendingRootAnchor={clearPendingScheduleRootAnchor}
            onUpdateComment={async (commentId, body) => {
              try {
                await updateComment.mutateAsync({
                  projectId: pid,
                  documentId: did,
                  commentId,
                  body,
                });
              } catch (error) {
                handleToast(commentErrorMessage(error, "edit"), "error");
                throw error;
              }
            }}
            onDeleteComment={(commentId) => {
              const target = allComments.find((comment) => comment.comment_id === commentId);
              const isThreadRoot = Boolean(target && !target.parent_comment_id);
              showAlertDialog({
                title: isThreadRoot ? "Delete this comment thread?" : "Delete this reply?",
                message: isThreadRoot
                  ? "This removes the thread and all replies for everyone on this project."
                  : "This removes the reply for everyone on this project.",
                type: "warning",
                actions: {
                  success: {
                    text: "Delete",
                    onSuccess: () => {
                      void (async () => {
                        try {
                          await deleteComment.mutateAsync({
                            projectId: pid,
                            documentId: did,
                            commentId,
                          });
                          handleToast(TOAST_MESSAGES.commentDeleted, "success");
                        } catch (error) {
                          handleToast(commentErrorMessage(error, "delete"), "error");
                        }
                      })();
                    },
                  },
                  cancel: "Cancel",
                },
              });
            }}
            isUpdatingComment={updateComment.isPending}
            isDeletingComment={deleteComment.isPending}
          />
        }
      />
    </>
  );
}
